import React from 'react';

export interface ArChipOption {
  value: string;
  label: string;
  color?: string;
  count?: number;
}

export interface ArFilters {
  tiers: Set<string>;
  mods: Set<string>;
  phases: Set<string>;
  statuses: Set<string>;
  q: string;
  leads: boolean;
}

export const emptyFilters = (): ArFilters => ({
  tiers: new Set(), mods: new Set(), phases: new Set(), statuses: new Set(), q: '', leads: false,
});

function toggle(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value); else next.add(value);
  return next;
}

const Chip: React.FC<{ active: boolean; onClick: () => void; color?: string; bar?: boolean; pill?: boolean; children: React.ReactNode }> = ({ active, onClick, color, bar, pill, children }) => (
  <button type="button" onClick={onClick} className="chip" aria-pressed={active}>
    {color && <span className={`sw${bar ? ' bar' : pill ? ' pill' : ''}`} aria-hidden="true" style={{ background: color }} />}
    {children}
  </button>
);

interface ArFilterBarProps {
  tierOptions: ArChipOption[];
  modOptions: ArChipOption[];
  phaseOptions: ArChipOption[];
  statusOptions?: ArChipOption[];
  showLeads?: boolean;
  hasLeads?: boolean;
  filters: ArFilters;
  onChange: (next: ArFilters) => void;
  searchPlaceholder?: string;
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="frow" role="group" aria-label={label}>
    <span className="fl cap">{label}</span>
    {children}
  </div>
);

const ArFilterBar: React.FC<ArFilterBarProps> = ({
  tierOptions, modOptions, phaseOptions, statusOptions, showLeads, hasLeads, filters, onChange, searchPlaceholder,
}) => {
  const active =
    filters.tiers.size + filters.mods.size + filters.phases.size + filters.statuses.size + (filters.q ? 1 : 0) + (filters.leads ? 1 : 0);

  return (
    <div className="filters">
      {tierOptions.length > 0 && (
        <Row label="Tier">
          {tierOptions.map((o) => (
            <Chip key={o.value} active={filters.tiers.has(o.value)} color={o.color} onClick={() => onChange({ ...filters, tiers: toggle(filters.tiers, o.value) })}>
              {o.label}
            </Chip>
          ))}
          {showLeads && hasLeads && (
            <Chip active={filters.leads} color="var(--lead)" onClick={() => onChange({ ...filters, leads: !filters.leads })}>
              Leads only
            </Chip>
          )}
        </Row>
      )}
      {modOptions.length > 0 && (
        <Row label="Modality">
          {modOptions.map((o) => (
            <Chip key={o.value} active={filters.mods.has(o.value)} onClick={() => onChange({ ...filters, mods: toggle(filters.mods, o.value) })}>
              {o.label}{o.count != null ? ` (${o.count})` : ''}
            </Chip>
          ))}
        </Row>
      )}
      {phaseOptions.length > 0 && (
        <Row label="Phase">
          {phaseOptions.map((o) => (
            <Chip key={o.value} active={filters.phases.has(o.value)} color={o.color} bar onClick={() => onChange({ ...filters, phases: toggle(filters.phases, o.value) })}>
              {o.label}
            </Chip>
          ))}
        </Row>
      )}
      {statusOptions && statusOptions.length > 0 && (
        <Row label="Data status">
          {statusOptions.map((o) => (
            <Chip key={o.value} active={filters.statuses.has(o.value)} color={o.color} pill onClick={() => onChange({ ...filters, statuses: toggle(filters.statuses, o.value) })}>
              {o.label}
            </Chip>
          ))}
        </Row>
      )}
      <div className="frow">
        <span className="fl cap">Find</span>
        <input
          className="search"
          type="search"
          value={filters.q}
          onChange={(e) => onChange({ ...filters, q: e.target.value })}
          placeholder={searchPlaceholder || 'Asset, target or sponsor'}
          aria-label="Filter text"
        />
        {active > 0 && (
          <button type="button" className="btn ghost" onClick={() => onChange(emptyFilters())}>Clear filters</button>
        )}
      </div>
    </div>
  );
};

export default ArFilterBar;
